import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPill from "@/components/base/FilterPill";
import Pagination from "@/components/base/Pagination";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { formatDotDate } from "@/utils/format";

const categories = ["全部", "影视", "生活", "活动", "绯闻"];
const ranges = [
  { key: "all", label: "全部时间", days: 9999 },
  { key: "7", label: "最近一周", days: 7 },
  { key: "30", label: "最近一个月", days: 30 },
  { key: "90", label: "最近三个月", days: 90 },
];
const sorts = [
  { key: "latest", label: "最新发布" },
  { key: "hot", label: "按热度" },
];
const PAGE_SIZE = 6;

export default function NewsListPage() {
  useDocumentMeta({
    title: "张柏芝新闻资讯 - 最新动态与报道",
    description:
      "实时更新张柏芝的新闻资讯，精彩报道助您及时掌握明星动态。按时间与类型筛选影视、生活、活动与传闻类报道。",
    keywords: "张柏芝新闻,张柏芝最新动态,张柏芝资讯,影视报道,娱乐新闻",
  });

  const [category, setCategory] = useState("全部");
  const [range, setRange] = useState("all");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    const rangeDays = ranges.find((item) => item.key === range)?.days ?? 9999;
    const now = new Date();
    const items = newsList.filter((item) => {
      const matchesCategory = category === "全部" || item.category === category;
      const diffDays = (now.getTime() - new Date(`${item.publishedAt}T09:00:00`).getTime()) / 86400000;
      return matchesCategory && diffDays <= rangeDays;
    });
    return [...items].sort((a, b) => {
      if (sort === "hot") return b.heat - a.heat;
      return b.publishedAt.localeCompare(a.publishedAt);
    });
  }, [category, range, sort]);

  useEffect(() => {
    setPage(1);
  }, [category, range, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hotList = newsList.filter((item) => item.id !== filtered[0]?.id).slice(0, 4);

  const reset = () => {
    setCategory("全部");
    setRange("all");
    setSort("latest");
  };

  return (
    <SiteLayout>
      <PageHero
        kicker="News Archive"
        title="张柏芝新闻"
        description="汇总来自公开报道的动态、访谈与活动现场记录，按发布时间与新闻类型整理，方便快速回溯每一次公开露面。"
      >
        <Breadcrumb items={[{ label: "新闻资讯" }]} />
      </PageHero>

      <div className="grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[260px_1fr] lg:px-12">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-600" aria-hidden="true"></i>
              筛选与排序
            </span>
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
          </button>

          <div className={`${filterOpen ? "mt-4 block" : "hidden"} lg:mt-0 lg:block`}>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-xs font-medium uppercase tracking-[0.22em] text-foreground-500">新闻类型</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((item) => (
                  <FilterPill key={item} label={item} active={category === item} onClick={() => setCategory(item)} />
                ))}
              </div>

              <div className="mt-6 border-t border-background-200 pt-5">
                <h2 className="text-xs font-medium uppercase tracking-[0.22em] text-foreground-500">时间范围</h2>
                <div className="mt-3 flex flex-col gap-1">
                  {ranges.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setRange(item.key)}
                      aria-pressed={range === item.key}
                      className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-xs transition-colors ${
                        range === item.key
                          ? "bg-primary-100 text-primary-800"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {item.label}
                      {range === item.key && <i className="ri-check-line" aria-hidden="true"></i>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 border-t border-background-200 pt-5">
                <h2 className="text-xs font-medium uppercase tracking-[0.22em] text-foreground-500">排序方式</h2>
                <div className="mt-3 grid grid-cols-2 gap-2 rounded-full bg-background-100 p-1">
                  {sorts.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      aria-pressed={sort === item.key}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-2 py-1.5 text-xs transition-colors ${
                        sort === item.key
                          ? "bg-background-50 text-primary-700"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={reset}
                className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                重置筛选条件
              </button>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="text-sm font-medium text-foreground-900">热门阅读</h2>
              <ul className="mt-3">
                {hotList.map((item, i) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/${item.id}`}
                      className="group flex items-start gap-3 border-b border-background-200 py-2.5 last:border-b-0"
                    >
                      <span className={`font-heading text-xs ${i === 0 ? "text-primary-600" : "text-foreground-400"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="line-clamp-2 flex-1 text-xs leading-5 text-foreground-700 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        <div>
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              共 <span className="font-medium text-foreground-900">{filtered.length}</span> 条结果
              {category !== "全部" && <span> · 类型：{category}</span>}
              {range !== "all" && <span> · {ranges.find((item) => item.key === range)?.label}</span>}
            </p>
            <p className="text-[11px] text-foreground-400">筛选与排序为前端演示逻辑，数据为站内示范内容</p>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 text-sm font-medium text-foreground-900">当前条件下没有找到新闻</p>
              <p className="mt-2 max-w-sm text-xs leading-6 text-foreground-500">
                可以尝试放宽时间范围，或切换到「全部」类型查看其它报道。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col divide-y divide-background-200 border-y border-background-200">
                {current.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group grid grid-cols-1 gap-4 py-6 sm:grid-cols-[220px_1fr] md:grid-cols-[260px_1fr]"
                  >
                    <div className="h-44 w-full overflow-hidden rounded-lg bg-background-200 sm:h-[150px] md:h-[168px]">
                      <img
                        src={item.cover}
                        alt={`${item.title} - 张柏芝新闻配图`}
                        title={`${item.title} - 张柏芝新闻`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                        <span className="rounded-sm bg-primary-100 px-2 py-0.5 text-primary-800">{item.category}</span>
                        <span>{formatDotDate(item.publishedAt)}</span>
                        <span className="text-foreground-300">·</span>
                        <span>{item.source}</span>
                      </div>
                      <h3 className="mt-2.5 font-heading text-base font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                        {item.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-[13px] leading-6 text-foreground-600">{item.summary}</p>
                      <div className="mt-auto flex items-center gap-4 pt-4 text-[11px] text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
                          热度 {item.heat}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-file-text-line" aria-hidden="true"></i>
                          {item.paragraphs.length} 段正文
                        </span>
                        <span className="ml-auto flex items-center gap-1 text-primary-700">
                          阅读全文
                          <i className="ri-arrow-right-line" aria-hidden="true"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={(next) => {
                  setPage(next);
                  window.scrollTo({ top: 260, behavior: "smooth" });
                }}
                note={`每页 ${PAGE_SIZE} 条（前端演示分页）`}
              />
            </>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}